import { useNode } from "@craftjs/core";
import React, { useContext, useEffect, useState } from "react";
import { isUpdatedFromDefault } from "../../utils";
import {
  AlignCenterIcon,
  AlignContentAroundIcon,
  AlignContentBetweenIcon,
  AlignContentCenterIcon,
  AlignContentEndIcon,
  AlignContentSpaceEvenIcon,
  AlignContentTopIcon,
  AlignEndIcon,
  AlignStartIcon,
  DisplayBlockIcon,
  FlexColumnIcon,
  FlexIcon,
  FlexNoWrapIcon,
  FlexRowIcon,
  FlexWrapIcon,
} from "../../../assets/ui-icons";
import {
  ColorInput,
  UnitInput,
  PaddingInput,
  Tabs,
  BorderRadiusInput,
  BorderInput,
  Spacer,
  AlignInput,
  ToggleInput,
  Panel,
  TextInput,
} from "../../input";
import { socialMedia } from "./socialMedia";
import { supportLibrary } from "../../block-supports";
import { INPUT_LIMITS } from "../../input-limits";
import SelectRadioButtonInput from "../../input/select-radio-buttons";
import { GrigoraEditorContext } from "../../editor/context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
} from "@fortawesome/free-solid-svg-icons";

export const SocialLinksSettings = () => {
  const {
    id,
    actions: { setProp },
    props,
    dom,
    supports,
  } = useNode((node) => ({
    id: node.id,
    text: node.data.props.text,
    props: node.data.props,
    dom: node.dom,
    supports: node.data?.type?.craft?.related?.supports,
  }));

  const {
    light,
    icons = [],
    links = {},
    layoutPadding = {
      top: "8px",
      bottom: "8px",
      left: "10px",
      right: "10px",
    },
    layoutPaddingTablet,
    layoutPaddingMobile,
    iconSize = "25px",
    iconSizeTablet,
    iconSizeMobile,
    color,
    backgroundColor,
    border,
    borderRadius = {
      topLeft: "20px",
      topRight: "20px",
      bottomLeft: "20px",
      bottomRight: "20px",
    },
    gapX = "6px",
    gapXTablet,
    gapXMobile,
    gapY = "10px",
    gapYTablet,
    gapYMobile,
    labelGap = "4px",
    labelGapTablet,
    labelGapMobile,
    typoFontSize,
    typoFontSizeTablet,
    typoFontSizeMobile,
    align,
    alignTablet,
    alignMobile,
    invertColor = true,
    columnLayout = false,
    columnLayoutTablet = false,
    columnLayoutMobile = false,
    openInNewTab = false,
    showLabel = false,
    isSocialShare = false,
    prefix,
    suffix,
    justifyContentTablet,
    justifyContentMobile,
    justifyContent,
  } = props;
  // console.log(columnLayout,columnLayoutTablet,columnLayoutMobile);
  const addIcon = (iconId) => {
    var tempIcons = [...icons];

    if (!tempIcons.includes(iconId)) {
      tempIcons.push(socialMedia[iconId].id);

      setProp((props) => {
        props.icons = tempIcons;
      });
    }
  };

  const removeIcon = (iconId) => {
    var tempIcons = [...icons];
    tempIcons = tempIcons.filter((id) => id !== iconId);
    setProp((props) => {
      props.icons = tempIcons;
    });
  };
  const SocialMediaLinksPicker = () => {
    return (
      <div className="p-1 flex flex-wrap justify-center gap-2">
        {Object.keys(socialMedia).map((item, i) => {
          return (
            <span
              onClick={(e) => {
                if (icons.includes(item)) removeIcon(item);
                else addIcon(item);
              }}
              key={`${socialMedia[item].name}-${i}`}
              className={`cursor-pointer select-none flex w-[32px] h-[32px] items-center justify-center hover:bg-gray-200 border border-transparent rounded-[4px] duration-200 text-gray-700 ${
                icons.includes(item) ? " border-ui-blue" : ""
              }`}
            >
              {light && socialMedia[item]?.iconLight
                ? socialMedia[item].iconLight
                : socialMedia[item]?.icon}
            </span>
          );
        })}
      </div>
    );
  };

  function GeneralSettings() {
    const [active, setActive] = useState("desktop");

    const editorContext = useContext(GrigoraEditorContext);

    useEffect(() => {
      if (editorContext?.displaySize) {
        setActive(editorContext.displaySize);
      }
    }, [editorContext?.displaySize]);

    const ALIGNITEMS = [
      {
        label: "Start",
        value: "start",
        tooltip: "Start",
        icon: <FontAwesomeIcon icon={faAlignLeft} size="lg" />,
      },
      {
        label: "Center",
        value: "center",
        tooltip: "Center",
        icon: <FontAwesomeIcon icon={faAlignCenter} size="lg" />,
      },
      {
        label: "End",
        value: "end",
        tooltip: "End",
        icon: <FontAwesomeIcon icon={faAlignRight} size="lg" />,
      },
    ];
    return (
      <div className="g-settings__wrapper">
        <Spacer spacing={"1rem"}>
          <SocialMediaLinksPicker />
        </Spacer>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: isSocialShare, default: false },
            { key: iconSize, default: "25px" },
            { key: iconSizeMobile },
            { key: iconSizeTablet },
            { key: gapX, default: "6px" },
            { key: gapXTablet },
            { key: gapXMobile },
            { key: gapY, default: "10px" },
            { key: gapYTablet },
            { key: gapYMobile },
            { key: align },
            { key: alignTablet },
            { key: alignMobile },
            { key: columnLayout, default: false },
            { key: columnLayoutTablet, default: false },
            { key: columnLayoutMobile, default: false },
            { key: showLabel, default: false },
            { key: prefix },
            { key: suffix },
            { key: typoFontSize },
            { key: typoFontSizeTablet },
            { key: typoFontSizeMobile },
            { key: labelGap, default: "4px" },
            { key: labelGapMobile },
            { key: labelGapTablet },
          ])}
          id={`block-setting-${id}-general`}
          title={"General"}
          initialOpen={false}
        >
          <ToggleInput
            label={"Use Light Icons"}
            value={light}
            onChange={(val) => {
              setProp((props) => {
                props.light = val;
              });
            }}
          />
          <ToggleInput
            label={"Social Share"}
            value={isSocialShare}
            onChange={(val) => {
              setProp((props) => {
                props.isSocialShare = val;
              });
            }}
          />
          <UnitInput
            isResponsive={true}
            resetValue={"25px"}
            value={iconSize}
            valueTablet={iconSizeTablet}
            valueMobile={iconSizeMobile}
            onChange={(val) => {
              setProp((props) => {
                props.iconSize = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.iconSizeTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.iconSizeMobile = val;
              });
            }}
            label={"Size"}
          />
          <UnitInput
            isResponsive={true}
            resetValue={"6px"}
            value={gapX}
            valueTablet={gapXTablet}
            valueMobile={gapXMobile}
            onChange={(val) => {
              setProp((props) => {
                props.gapX = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.gapXTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.gapXMobile = val;
              });
            }}
            label={"Gap X"}
          />
          <UnitInput
            isResponsive={true}
            resetValue={"10px"}
            value={gapY}
            valueTablet={gapYTablet}
            valueMobile={gapYMobile}
            onChange={(val) => {
              setProp((props) => {
                props.gapY = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.gapYTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.gapYMobile = val;
              });
            }}
            label={"Gap Y"}
          />
          <ToggleInput
            isResponsive={true}
            value={columnLayout}
            valueMobile={columnLayoutMobile}
            valueTablet={columnLayoutTablet}
            onChange={(val) => {
              setProp((props) => {
                props.columnLayout = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.columnLayoutTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.columnLayoutMobile = val;
              });
            }}
            label={"Column Layout"}
          />
          {(
            active === "desktop"
              ? columnLayout
              : active === "tablet"
              ? columnLayoutTablet
              : columnLayoutMobile
          ) ? (
            <AlignInput
              isResponsive={true}
              value={justifyContent}
              valueTablet={justifyContentTablet}
              valueMobile={justifyContentMobile}
              onChange={(val) => {
                setProp((props) => {
                  props.justifyContent = val;
                });
              }}
              setValueTablet={(val) => {
                setProp((props) => {
                  props.justifyContentTablet = val;
                });
              }}
              setValueMobile={(val) => {
                setProp((props) => {
                  props.justifyContentMobile = val;
                });
              }}
              label={"Align"}
            />
          ) : (
            <SelectRadioButtonInput
              isResponsive={true}
              valueTablet={alignTablet}
              valueMobile={alignMobile}
              resetValue={undefined}
              value={align}
              onChange={(val) => {
                setProp((props) => {
                  props.align = val;
                });
              }}
              setValueTablet={(val) => {
                setProp((props) => {
                  props.alignTablet = val;
                });
              }}
              setValueMobile={(val) => {
                setProp((props) => {
                  props.alignMobile = val;
                });
              }}
              options={ALIGNITEMS}
              label="Align"
            />
          )}

          <ToggleInput
            value={showLabel}
            onChange={(val) => {
              setProp((props) => {
                props.showLabel = val;
              });
            }}
            label={"Show Label"}
          />
          {showLabel && (
            <>
              <TextInput
                minLength={INPUT_LIMITS["social-link-prefix"].minlength}
                maxLength={INPUT_LIMITS["social-link-prefix"].maxlength}
                onChange={(val) => {
                  setProp((props) => {
                    props.prefix = val;
                  });
                }}
                label={"Prefix"}
                value={prefix}
              />

              <TextInput
                minLength={INPUT_LIMITS["social-link-suffix"].minlength}
                maxLength={INPUT_LIMITS["social-link-suffix"].maxlength}
                onChange={(val) => {
                  setProp((props) => {
                    props.suffix = val;
                  });
                }}
                label={"Suffix"}
                value={suffix}
              />
              <UnitInput
                isResponsive={true}
                isResetVisible={false}
                value={typoFontSize}
                valueTablet={typoFontSizeTablet}
                valueMobile={typoFontSizeMobile}
                onChange={(val) => {
                  setProp((props) => {
                    props.typoFontSize = val;
                  });
                }}
                setValueTablet={(val) => {
                  setProp((props) => {
                    props.typoFontSizeTablet = val;
                  });
                }}
                setValueMobile={(val) => {
                  setProp((props) => {
                    props.typoFontSizeMobile = val;
                  });
                }}
                label={"Label Size"}
              />

              <UnitInput
                isResponsive={true}
                resetValue={"4px"}
                value={labelGap}
                valueTablet={labelGapTablet}
                valueMobile={labelGapMobile}
                onChange={(val) => {
                  setProp((props) => {
                    props.labelGap = val;
                  });
                }}
                setValueTablet={(val) => {
                  setProp((props) => {
                    props.labelGapTablet = val;
                  });
                }}
                setValueMobile={(val) => {
                  setProp((props) => {
                    props.labelGapMobile = val;
                  });
                }}
                label={"Label Gap"}
              />
            </>
          )}
        </Panel>
        {!isSocialShare && (
          <Panel
            isUpdated={isUpdatedFromDefault([
              { key: openInNewTab, default: false },
              { key: backgroundColor },
            ])}
            id={`block-setting-${id}-links`}
            title={"Links"}
            initialOpen={false}
          >
            <ToggleInput
              value={openInNewTab}
              onChange={(val) => {
                setProp((props) => {
                  props.openInNewTab = val;
                });
              }}
              label={"Open in new tab"}
            />
            {icons &&
              icons.map((iconId, i) => {
                return (
                  <TextInput
                    minLength={INPUT_LIMITS["social-links"].minlength}
                    maxLength={INPUT_LIMITS["social-links"].maxlength}
                    key={`${socialMedia[iconId]?.id}-${i}`}
                    value={links[socialMedia[iconId]?.id]}
                    onChange={(val) => {
                      var temp = { ...(links ? links : {}) };
                      temp[iconId] = val;

                      setProp((props) => {
                        props.links = temp;
                      });
                    }}
                    label={socialMedia[iconId]?.name}
                  />
                );
              })}
          </Panel>
        )}
      </div>
    );
  }

  function StyleSettings() {
    return (
      <>
        <div className="g-settings__wrapper">
          <Spacer spacing="1rem">
            <ToggleInput
              value={invertColor}
              onChange={(val) => {
                setProp((props) => {
                  props.invertColor = val;
                });
              }}
              label={"Invert Color"}
            />
            <ColorInput
              value={color}
              onChange={(val) => {
                setProp((props) => {
                  props.color = val;
                });
              }}
              label={"Color"}
            />
            <ColorInput
              value={backgroundColor}
              onChange={(val) => {
                setProp((props) => {
                  props.backgroundColor = val;
                });
              }}
              label={"Background"}
            />
          </Spacer>
        </div>
      </>
    );
  }

  function AdvancedSettings() {
    return (
      <>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: layoutPaddingMobile },
            { key: layoutPaddingTablet },
            { key: layoutPadding },
          ])}
          id={`block-setting-${id}-layout`}
          title={"Layout"}
          initialOpen={true}
        >
          <PaddingInput
            resetValue={{
              top: "8px",
              bottom: "8px",
              left: "10px",
              right: "10px",
            }}
            label={"Padding"}
            isResponsive={true}
            valueTablet={layoutPaddingTablet}
            valueMobile={layoutPaddingMobile}
            value={layoutPadding}
            onChange={(val) => {
              setProp((props) => {
                props.layoutPadding = val;
              });
            }}
            setValueTablet={(val) => {
              setProp((props) => {
                props.layoutPaddingTablet = val;
              });
            }}
            setValueMobile={(val) => {
              setProp((props) => {
                props.layoutPaddingMobile = val;
              });
            }}
          />
        </Panel>
        <Panel
          isUpdated={isUpdatedFromDefault([
            { key: border },
            { key: borderRadius },
          ])}
          id={`block-setting-${id}-border`}
          title={"Border"}
          initialOpen={true}
        >
          <BorderInput
            resetValue={undefined}
            value={border}
            onChange={(val) => {
              setProp((props) => {
                props.border = val;
              });
            }}
            label={"Border"}
          />
          <BorderRadiusInput
            resetValue={{
              topLeft: "50%",
              topRight: "50%",
              bottomLeft: "50%",
              bottomRight: "50%",
            }}
            value={borderRadius}
            onChange={(val) => {
              setProp((props) => {
                props.borderRadius = val;
              });
            }}
            label={"Border Radius"}
          />
        </Panel>
        {supports &&
          supports.map((support) => {
            var Component = supportLibrary[support]?.component;

            if (!Component) {
              return;
            }

            return (
              <>
                <Component
                  block={{
                    id,
                    props,
                    dom,
                  }}
                  actions={{
                    setProp,
                  }}
                ></Component>
              </>
            );
          })}
      </>
    );
  }
  const tabs = [
    {
      label: "General",
      key: "general",
      children: GeneralSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: isSocialShare, default: false },
        { key: iconSize, default: "25px" },
        { key: iconSizeMobile },
        { key: iconSizeTablet },
        { key: gapX, default: "6px" },
        { key: gapXTablet },
        { key: gapXMobile },
        { key: gapY, default: "10px" },
        { key: gapYTablet },
        { key: gapYMobile },
        { key: align },
        { key: alignTablet },
        { key: alignMobile },
        { key: columnLayout, default: false },
        { key: showLabel, default: false },
        { key: prefix },
        { key: suffix },
        { key: typoFontSize },
        { key: typoFontSizeTablet },
        { key: typoFontSizeMobile },
        { key: labelGap, default: "4px" },
        { key: labelGapMobile },
        { key: labelGapTablet },
        { key: openInNewTab, default: false },
        { key: backgroundColor },
        { key: justifyContentMobile },
        { key: justifyContentTablet },
        { key: justifyContent },
        { key: columnLayoutTablet, default: false },
        { key: columnLayoutMobile, default: false },
        { key: columnLayout, default: false },
      ]),
    },
    {
      label: "Style",
      key: "style",
      children: StyleSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: invertColor },
        { key: color },
        { key: backgroundColor },
      ]),
    },
    {
      label: "Advanced",
      key: "advanced",
      children: AdvancedSettings(),
      isUpdated: isUpdatedFromDefault([
        { key: layoutPaddingTablet },
        { key: layoutPaddingMobile },
        { key: layoutPadding },
        { key: border },
        { key: borderRadius },
      ]),
    },
  ];

  return (
    <div>
      <Tabs
        type="block-setting"
        id={`block-settings-tab-${id}`}
        items={tabs.map((item, i) => {
          return {
            label: item.label,
            key: item.key,
            children: item.children,
          };
        })}
      />
    </div>
  );
};
